import React, { useEffect, useState } from "react";
import { api, fmtDay, fmtCurrency } from "@/lib/api";
import { Link } from "react-router-dom";
import { Truck, Fuel, Wrench, MapPin, Plus, Activity, CheckCircle2, Clock, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

const STATUS_COLORS = {
  preparing_loading: "#A1A1AA", loaded: "#3B82F6", departed: "#3B82F6",
  arrived_venue: "#D4AF37", setup_started: "#F59E0B", setup_completed: "#10B981",
  returning: "#3B82F6", returned_warehouse: "#10B981",
};

const VEHICLE_STATUS = {
  available: "#10B981", in_use: "#3B82F6", maintenance: "#F59E0B",
};

export default function LogisticsPage() {
  const [tab, setTab] = useState("deliveries");
  const [vehicles, setVehicles] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [vForm, setVForm] = useState({ open: false, vehicle: null });
  const [showDForm, setShowDForm] = useState(false);

  const load = async () => {
    const [v, d, o] = await Promise.all([
      api.get("/vehicles"), api.get("/deliveries"), api.get("/orders"),
    ]);
    setVehicles(v.data); setDeliveries(d.data); setOrders(o.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="logistics-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Fleet & Routes</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Logistics</h1>
        </div>
        <div className="flex gap-2">
          {tab === "deliveries" ? (
            <Button onClick={() => setShowDForm(true)} data-testid="new-delivery" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" /> Dispatch
            </Button>
          ) : (
            <Button onClick={() => setVForm({ open: true, vehicle: null })} data-testid="new-vehicle" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" /> Add Vehicle
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-[#1a1a1a]">
        {["deliveries", "vehicles"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-3 text-sm capitalize border-b-2 transition-colors ${tab === t ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {t} <span className="text-xs text-zinc-600 ml-1">({t === "deliveries" ? deliveries.length : vehicles.length})</span>
          </button>
        ))}
      </div>

      {tab === "deliveries" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {deliveries.length === 0 && <div className="col-span-full card-lux p-10 text-center text-zinc-600 text-sm">No active dispatches. Click "Dispatch" to create one from an order.</div>}
          {deliveries.map((d) => {
            const order = orders.find((o) => o.id === d.order_id);
            const vehicle = vehicles.find((v) => v.id === d.vehicle_id);
            const checked = (d.loading_checklist || []).filter((i) => i.checked).length;
            const total = (d.loading_checklist || []).length;
            return (
              <Link key={d.id} to={`/delivery/${d.id}`} className="card-lux p-5 block hover:-translate-y-0.5 transition-transform" data-testid={`delivery-${d.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="label-eyebrow">{order?.event_type}</div>
                    <div className="font-display text-xl mt-1">{order?.customer_name || "—"}</div>
                    <div className="text-xs text-zinc-500 mt-1">{order ? fmtDay(order.event_date) : ""}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className="stage-chip" style={{ color: STATUS_COLORS[d.status], borderColor: STATUS_COLORS[d.status] + "55", background: STATUS_COLORS[d.status] + "10" }}>
                      {d.status.replace(/_/g, " ")}
                    </span>
                    {order && <StatusBadge status={order.status} />}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[#1a1a1a] text-xs">
                  <div><div className="text-zinc-500">Vehicle</div><div className="text-zinc-200 mt-0.5">{vehicle?.plate || "—"}</div></div>
                  <div><div className="text-zinc-500">Loading</div><div className="text-[#D4AF37] mt-0.5">{checked}/{total}</div></div>
                  <div><div className="text-zinc-500">Pings</div><div className="text-zinc-200 mt-0.5">{(d.gps_pings || []).length}</div></div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((v) => (
            <div key={v.id} className="card-lux p-5" data-testid={`vehicle-${v.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <div className="font-display text-xl">{v.plate}</div>
                    <div className="text-xs text-zinc-500">{v.name}</div>
                  </div>
                </div>
                <span className="stage-chip capitalize" style={{ color: VEHICLE_STATUS[v.status], borderColor: VEHICLE_STATUS[v.status] + "55", background: VEHICLE_STATUS[v.status] + "10" }}>{v.status}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-zinc-500">Capacity</div><div className="text-zinc-200 mt-0.5">{v.capacity_pax} pax</div></div>
                <div><div className="text-zinc-500">Mileage</div><div className="text-zinc-200 mt-0.5">{v.mileage_km.toLocaleString()} km</div></div>
                <div className="col-span-2">
                  <div className="text-zinc-500 flex items-center gap-1.5"><Fuel className="w-3 h-3" /> Fuel</div>
                  <div className="mt-1 h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
                    <div className="h-full" style={{ width: `${v.fuel_level}%`, background: v.fuel_level > 40 ? "#10B981" : v.fuel_level > 20 ? "#F59E0B" : "#EF4444" }} />
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">{v.fuel_level}%</div>
                </div>
                {v.next_maintenance && (
                  <div className="col-span-2 pt-2 border-t border-[#1a1a1a]">
                    <div className="text-zinc-500 flex items-center gap-1.5"><Wrench className="w-3 h-3" /> Next service</div>
                    <div className="text-amber-400 mt-0.5">{fmtDay(v.next_maintenance)}</div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-[#1a1a1a]">
                <button onClick={() => setVForm({ open: true, vehicle: v })} data-testid={`vehicle-edit-${v.id}`} title="Edit vehicle" className="p-1.5 rounded-sm text-zinc-500 hover:text-[#D4AF37] hover:bg-[#151515] transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete vehicle ${v.plate}?`)) return;
                    try {
                      await api.delete(`/vehicles/${v.id}`);
                      toast.success("Vehicle deleted");
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to delete");
                    }
                  }}
                  data-testid={`vehicle-delete-${v.id}`}
                  title="Delete vehicle"
                  className="p-1.5 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-[#151515] transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DeliveryForm open={showDForm} orders={orders} vehicles={vehicles} onClose={() => setShowDForm(false)} onSaved={() => { setShowDForm(false); load(); toast.success("Dispatch created"); }} />
      <VehicleForm open={vForm.open} vehicle={vForm.vehicle} onClose={() => setVForm({ open: false, vehicle: null })} onSaved={() => { setVForm({ open: false, vehicle: null }); load(); toast.success("Vehicle saved"); }} />
    </div>
  );
}

function VehicleForm({ open, vehicle, onClose, onSaved }) {
  const blankV = { plate: "", name: "", capacity_pax: 200, fuel_level: 100, mileage_km: 0, status: "available", next_maintenance: "" };
  const [f, setF] = useState(blankV);
  useEffect(() => {
    if (!open) return;
    setF(vehicle ? {
      plate: vehicle.plate || "", name: vehicle.name || "",
      capacity_pax: vehicle.capacity_pax || 0, fuel_level: vehicle.fuel_level ?? 100,
      mileage_km: vehicle.mileage_km || 0, status: vehicle.status || "available",
      next_maintenance: (vehicle.next_maintenance || "").slice(0, 10),
    } : blankV);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vehicle]);

  const save = async () => {
    try {
      const payload = { ...f, next_maintenance: f.next_maintenance || null };
      if (vehicle) await api.patch(`/vehicles/${vehicle.id}`, payload);
      else await api.post("/vehicles", payload);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save vehicle");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white">
        <DialogHeader><DialogTitle className="font-display text-2xl">{vehicle ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Plate (HNC-004)" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value })} data-testid="v-plate" className="bg-[#050505] border-[#2a2a2a]" />
          <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="v-name" className="bg-[#050505] border-[#2a2a2a]" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow text-[10px]">Capacity (pax)</Label>
              <Input type="number" value={f.capacity_pax} onChange={(e) => setF({ ...f, capacity_pax: Number(e.target.value) })} data-testid="v-capacity" className="bg-[#050505] border-[#2a2a2a] mt-1" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Mileage (km)</Label>
              <Input type="number" value={f.mileage_km} onChange={(e) => setF({ ...f, mileage_km: Number(e.target.value) })} data-testid="v-mileage" className="bg-[#050505] border-[#2a2a2a] mt-1" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Fuel level (%)</Label>
              <Input type="number" min="0" max="100" value={f.fuel_level} onChange={(e) => setF({ ...f, fuel_level: Math.max(0, Math.min(100, Number(e.target.value))) })} data-testid="v-fuel" className="bg-[#050505] border-[#2a2a2a] mt-1" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Status</Label>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="v-status" className="w-full mt-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
                {Object.keys(VEHICLE_STATUS).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow text-[10px]">Next service date (optional)</Label>
              <Input type="date" value={f.next_maintenance} onChange={(e) => setF({ ...f, next_maintenance: e.target.value })} data-testid="v-maintenance" className="bg-[#050505] border-[#2a2a2a] mt-1" />
            </div>
          </div>
          <Button onClick={save} disabled={!f.plate} data-testid="v-save" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] w-full">{vehicle ? "Save Changes" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryForm({ open, orders, vehicles, onClose, onSaved }) {
  const [orderId, setOrderId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const eligible = orders.filter((o) => !["completed"].includes(o.status));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white">
        <DialogHeader><DialogTitle className="font-display text-2xl">Dispatch Order</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px]">Order</Label>
            <select value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="d-order" className="w-full mt-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
              <option value="">— Select —</option>
              {eligible.map((o) => <option key={o.id} value={o.id}>{o.customer_name} · {fmtDay(o.event_date)}</option>)}
            </select>
          </div>
          <div>
            <Label className="label-eyebrow text-[10px]">Vehicle</Label>
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} data-testid="d-vehicle" className="w-full mt-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
              <option value="">— Select —</option>
              {vehicles.filter((v) => v.status !== "maintenance").map((v) => <option key={v.id} value={v.id}>{v.plate} · {v.name}</option>)}
            </select>
          </div>
          <Button disabled={!orderId} onClick={async () => { await api.post("/deliveries", { order_id: orderId, vehicle_id: vehicleId || null }); onSaved?.(); }} data-testid="d-save" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] w-full">Create Dispatch</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
